import Fuse from 'fuse.js'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { API_GET_GALLERY_LIST_URL } from './constants'
import UserContext, { UserContextType } from './UserContext'
import { shuffle } from './util'
import Gallery, { GalleryItem } from './Gallery'
import realtimeService from './RealtimeService'
const fuseOptions = {
  keys: ['name', 'type'],
  includeScore: true,
  includeMatches: true,
}

const GalleryContainer = () => {
  const navigate = useNavigate()
  const {
    userContext: { isShowGallery, isReadOnly },
  } = useContext(UserContext) as UserContextType
  const [directory, setDirectory] = useState<GalleryItem[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [fuse, setFuse] = React.useState<Fuse<GalleryItem>>(new Fuse([], fuseOptions))
  const [slideShowIntervalId, setSlideShowIntervalId] = useState<NodeJS.Timer | null>(null)
  const [shuffleCount, setShuffleCount] = useState(0)
  useEffect(() => {
    initDirectory()
  }, [])

  useEffect(() => {
    return () => {
      if (slideShowIntervalId) {
        clearTimeout(slideShowIntervalId)
      }
    }
  }, [slideShowIntervalId])
  const displayedWorks = React.useMemo(() => {
    const worksToDisplay = searchQuery.trim()
      ? fuse.search(searchQuery).map((result) => result.item)
      : [...directory]
    if (shuffleCount > 0) {
      shuffle(worksToDisplay)
    }
    return worksToDisplay
  }, [searchQuery, fuse, shuffleCount, directory])

  const initDirectory = async () => {
    const urlResponse = await fetch(API_GET_GALLERY_LIST_URL)
    const { root } = await urlResponse.json()
    realtimeService.endpointOnce('blacklist', {}).then((res) => {
      const blacklistMap = res as { [type: string]: string[] }
      const galleryIndex: GalleryItem[] = []
      Object.keys(root).forEach((dirName: string) => {
        const blacklist = blacklistMap[dirName] || []
        const gallerySet = root[dirName].map((itemName: string) => {
          const item: GalleryItem = {
            type: dirName,
            name: itemName,
          }
          if (blacklist.includes(itemName)) {
            item.isBlacklisted = true
          }
          return item
        })
        galleryIndex.push(...gallerySet)
      })
      setDirectory(galleryIndex)
      setFuse(new Fuse(galleryIndex, fuseOptions))
    })
  }
  const SHUFFLE_MILLIS = 3000
  const togglePlay = () => {
    if (slideShowIntervalId) {
      clearTimeout(slideShowIntervalId)
      setSlideShowIntervalId(null)
      return
    }
    if (!displayedWorks.length) {
      return
    }
    let nextIndex = 0
    const id = setInterval(() => {
      const {
        type,
        name,
        isBlacklisted = false,
      } = displayedWorks[nextIndex++ % displayedWorks.length]
      const path = `archive/${type}/${name}`
      if (isBlacklisted && isReadOnly) {
        console.log(`SKIPPING ${path}`)
      } else {
        console.log(`navigating to ${path}`)
        navigate(path)
      }
    }, SHUFFLE_MILLIS)
    setSlideShowIntervalId(id)
  }
  const doShuffle = () => {
    setShuffleCount(shuffleCount + 1)
  }
  const setQuery = (query: string) => {
    setSearchQuery(query)
    setShuffleCount(0)
  }
  if (!isShowGallery) {
    return null
  }
  return (
    <Gallery
      displayedWorks={displayedWorks}
      isPlaying={!!slideShowIntervalId}
      togglePlay={togglePlay}
      searchQuery={searchQuery}
      setSearchQuery={setQuery}
      doShuffle={doShuffle}
    />
  )
}

export default GalleryContainer
